import type { NotificationPreferences } from '../types'

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  soundEnabled: true,
  volume: 0.7,
  desktopEnabled: false,
  soundSource: 'default',
  customSoundName: '',
}

export const DEFAULT_SOUND_URL = '/sounds/kakinn.mp3'

const preferencesKey = (identity: string) => `mugen2.notifications.${identity || 'local'}`
const DATABASE_NAME = 'mugen2-assets'
const STORE_NAME = 'notification-sounds'

export function readNotificationPreferences(identity: string): NotificationPreferences {
  try {
    const raw = JSON.parse(localStorage.getItem(preferencesKey(identity)) || '{}') as Partial<NotificationPreferences>
    return {
      soundEnabled: raw.soundEnabled !== false,
      volume: Number.isFinite(Number(raw.volume)) ? Math.max(0, Math.min(1, Number(raw.volume))) : 0.7,
      desktopEnabled: raw.desktopEnabled === true,
      soundSource: raw.soundSource === 'custom' ? 'custom' : 'default',
      customSoundName: typeof raw.customSoundName === 'string' ? raw.customSoundName : '',
    }
  } catch {
    return { ...DEFAULT_NOTIFICATION_PREFERENCES }
  }
}

export function writeNotificationPreferences(identity: string, preferences: NotificationPreferences): void {
  localStorage.setItem(preferencesKey(identity), JSON.stringify(preferences))
}

const openDatabase = (): Promise<IDBDatabase> => new Promise((resolve, reject) => {
  const request = indexedDB.open(DATABASE_NAME, 1)
  request.addEventListener('upgradeneeded', () => {
    if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME)
  })
  request.addEventListener('success', () => resolve(request.result))
  request.addEventListener('error', () => reject(request.error ?? new Error('IndexedDB open failed')))
})

export async function saveCustomSound(identity: string, file: File): Promise<void> {
  const database = await openDatabase()
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite')
    transaction.objectStore(STORE_NAME).put(file, identity || 'local')
    transaction.addEventListener('complete', () => resolve())
    transaction.addEventListener('error', () => reject(transaction.error ?? new Error('Sound save failed')))
  })
  database.close()
}

async function readCustomSound(identity: string): Promise<Blob | null> {
  const database = await openDatabase()
  const result = await new Promise<Blob | null>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readonly')
    const request = transaction.objectStore(STORE_NAME).get(identity || 'local')
    request.addEventListener('success', () => resolve(request.result instanceof Blob ? request.result : null))
    request.addEventListener('error', () => reject(request.error ?? new Error('Sound read failed')))
  })
  database.close()
  return result
}

export async function playCompletionSound(
  identity: string,
  preferences: NotificationPreferences,
): Promise<void> {
  if (!preferences.soundEnabled) return
  let source = DEFAULT_SOUND_URL
  let objectUrl = ''
  if (preferences.soundSource === 'custom') {
    const blob = await readCustomSound(identity)
    if (blob) {
      objectUrl = URL.createObjectURL(blob)
      source = objectUrl
    }
  }
  const audio = new Audio(source)
  audio.volume = Math.max(0, Math.min(1, preferences.volume))
  try {
    await audio.play()
  } finally {
    if (objectUrl) {
      audio.addEventListener('ended', () => URL.revokeObjectURL(objectUrl), { once: true })
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    }
  }
}

export function desktopPermission(): NotificationPermission | 'unsupported' {
  return 'Notification' in window ? Notification.permission : 'unsupported'
}

export async function requestDesktopPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (!('Notification' in window)) return 'unsupported'
  return await Notification.requestPermission()
}

export function showDesktopCompletion(count: number, serverNames: string[]): void {
  if (!('Notification' in window) || Notification.permission !== 'granted') return
  const uniqueServers = [...new Set(serverNames.filter(Boolean))]
  const body = count === 1
    ? `${uniqueServers[0] || 'ComfyUI'} の動画が完成しました。`
    : `${count} Jobs が完成しました${uniqueServers.length ? `・${uniqueServers.join(' / ')}` : ''}。`
  new Notification('Mugen Video・Generation complete', { body })
}
